import { useState, useEffect, useCallback } from 'react'
import { usePostHog } from '../../contexts/PostHogContext'
import { motion, AnimatePresence } from 'framer-motion'

const CONSENT_KEY = 'candlefish_analytics_consent'
const PREFERENCES_KEY = 'candlefish_cookie_preferences'

interface CookiePreferences {
  essential: boolean
  analytics: boolean
  performance: boolean
}

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    performance: false,
  })
  const posthog = usePostHog()

  const applyConsent = useCallback((prefs: CookiePreferences) => {
    if (!posthog) return
    if (prefs.analytics || prefs.performance) {
      posthog.opt_in_capturing()
      posthog.capture('analytics_consent_granted', {
        analytics_enabled: prefs.analytics,
        performance_enabled: prefs.performance,
      })
    } else {
      posthog.opt_out_capturing()
    }
  }, [posthog])

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY)
    const savedPreferences = localStorage.getItem(PREFERENCES_KEY)
    if (savedPreferences) {
      const prefs = JSON.parse(savedPreferences)
      setPreferences(prefs)
      applyConsent(prefs)
    } else if (!consent) {
      setTimeout(() => setShowBanner(true), 1500)
    }
  }, [applyConsent])

  // Listen for external open requests (e.g. from Privacy page)
  useEffect(() => {
    const handleOpen = () => {
      const saved = localStorage.getItem(PREFERENCES_KEY)
      if (saved) setPreferences(JSON.parse(saved))
      setShowPreferences(true)
      setShowBanner(true)
    }
    window.addEventListener('open-cookie-preferences', handleOpen)
    return () => window.removeEventListener('open-cookie-preferences', handleOpen)
  }, [])

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(CONSENT_KEY, 'set')
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs))
    setPreferences(prefs)
    applyConsent(prefs)
    setShowBanner(false)
    setShowPreferences(false)
  }

  const handleAcceptAll = () => savePreferences({ essential: true, analytics: true, performance: true })
  const handleEssentialOnly = () => savePreferences({ essential: true, analytics: false, performance: false })

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 20, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 20, opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: [0.6, -0.05, 0.01, 0.99] }}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[100] w-[min(360px,calc(100vw-2rem))]"
        >
          <div className="border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-black rounded-[5px]">
            {!showPreferences ? (
              /* ─── Compact Banner ─── */
              <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-semibold text-black dark:text-white">
                    Privacy & Cookies
                  </h3>
                  <button
                    onClick={handleEssentialOnly}
                    className="text-neutral-400 hover:text-black dark:hover:text-white transition-colors ml-2"
                    aria-label="Dismiss"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <p className="text-xs text-neutral-600 dark:text-neutral-400 font-normal leading-relaxed mb-4">
                  We use cookies to understand how you use our site and improve your experience.
                </p>

                <div className="flex gap-2 mb-3">
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-3 py-2.5 min-h-[44px] bg-[#0f637b] dark:bg-[#04daf6] text-white dark:text-black rounded-[5px] text-xs font-semibold transition-all hover:opacity-80"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleEssentialOnly}
                    className="flex-1 px-3 py-2.5 min-h-[44px] border-2 border-neutral-200 dark:border-neutral-600 text-black dark:text-white rounded-[5px] text-xs font-semibold transition-all hover:border-neutral-400 dark:hover:border-neutral-400"
                  >
                    Essential Only
                  </button>
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <a
                    href="/privacy"
                    className="text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white transition-colors underline underline-offset-2"
                  >
                    Privacy Policy
                  </a>
                  <span className="text-neutral-600 dark:text-neutral-400">·</span>
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white transition-colors underline underline-offset-2"
                  >
                    Manage Preferences
                  </button>
                </div>
              </div>
            ) : (
              /* ─── Preferences Panel ─── */
              <div className="p-4 sm:p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-black dark:text-white">
                    Cookie Preferences
                  </h3>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="text-neutral-400 hover:text-black dark:hover:text-white transition-colors"
                    aria-label="Back"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-3">
                  {/* Essential */}
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-xs font-semibold text-black dark:text-white">Essential</p>
                      <p className="text-[10px] text-neutral-500 mt-0.5">Required for site to work</p>
                    </div>
                    <span className="text-[10px] font-semibold text-[#0f637b] dark:text-[#04daf6] uppercase tracking-wider">Always On</span>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between py-2 border-t border-neutral-100 dark:border-neutral-800">
                    <div>
                      <p className="text-xs font-semibold text-black dark:text-white">Analytics</p>
                      <p className="text-[10px] text-neutral-500 mt-0.5">Page views & navigation</p>
                    </div>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        preferences.analytics ? 'bg-[#0f637b] dark:bg-[#04daf6]' : 'bg-neutral-200 dark:bg-neutral-700'
                      }`}
                      aria-label="Toggle analytics"
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        preferences.analytics ? 'translate-x-[18px]' : 'translate-x-[3px]'
                      }`} />
                    </button>
                  </div>

                  {/* Performance */}
                  <div className="flex items-center justify-between py-2 border-t border-neutral-100 dark:border-neutral-800">
                    <div>
                      <p className="text-xs font-semibold text-black dark:text-white">Performance</p>
                      <p className="text-[10px] text-neutral-500 mt-0.5">Speed & error tracking</p>
                    </div>
                    <button
                      onClick={() => togglePreference('performance')}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        preferences.performance ? 'bg-[#0f637b] dark:bg-[#04daf6]' : 'bg-neutral-200 dark:bg-neutral-700'
                      }`}
                      aria-label="Toggle performance"
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        preferences.performance ? 'translate-x-[18px]' : 'translate-x-[3px]'
                      }`} />
                    </button>
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-800">
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="flex-1 px-3 py-2.5 min-h-[44px] border-2 border-neutral-200 dark:border-neutral-600 text-black dark:text-white rounded-[5px] text-xs font-semibold transition-all hover:border-neutral-400 dark:hover:border-neutral-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => savePreferences(preferences)}
                    className="flex-1 px-3 py-2.5 min-h-[44px] bg-[#0f637b] dark:bg-[#04daf6] text-white dark:text-black rounded-[5px] text-xs font-semibold transition-all hover:opacity-80"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
